// Lightweight panel auth context — uses localStorage to persist tokens between visits.
// Cookies are not used because the existing app already uses Authorization header for /api/admin/* (X-Admin-Token).
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/panel`;
const GW_KEY = 'entorid_gw_token';
const PANEL_KEY = 'entorid_panel_token';
const USER_KEY = 'entorid_panel_user';

const PanelAuthContext = createContext(null);

export function PanelAuthProvider({ children }) {
  const [gwToken, setGwToken] = useState(() => localStorage.getItem(GW_KEY) || '');
  const [panelToken, setPanelToken] = useState(() => localStorage.getItem(PANEL_KEY) || '');
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
  });
  const [bootChecked, setBootChecked] = useState(false);

  // Validate stored tokens on first mount
  useEffect(() => {
    let cancel = false;
    (async () => {
      if (panelToken) {
        try {
          const res = await axios.get(`${API_BASE}/me`, { headers: { Authorization: `Bearer ${panelToken}` }, timeout: 6000 });
          if (!cancel) setUser(res.data);
        } catch (e) {
          if (!cancel) {
            setPanelToken(''); localStorage.removeItem(PANEL_KEY);
            setUser(null); localStorage.removeItem(USER_KEY);
          }
        }
      }
      if (gwToken) {
        try {
          await axios.get(`${API_BASE}/gateway/me`, { headers: { 'X-Gateway-Token': gwToken }, timeout: 6000 });
        } catch (e) {
          if (!cancel) {
            setGwToken(''); localStorage.removeItem(GW_KEY);
          }
        }
      }
      if (!cancel) setBootChecked(true);
    })();
    return () => { cancel = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const gatewayLogin = useCallback(async (email, password, remember = true) => {
    const res = await axios.post(`${API_BASE}/gateway/login`, { email, password, remember_me: remember });
    setGwToken(res.data.gateway_token);
    localStorage.setItem(GW_KEY, res.data.gateway_token);
    return res.data;
  }, []);

  const panelLogin = useCallback(async (identifier, password, remember = true) => {
    const res = await axios.post(`${API_BASE}/login`,
      { identifier, password, remember_me: remember },
      { headers: { 'X-Gateway-Token': gwToken } },
    );
    setPanelToken(res.data.token);
    setUser(res.data.user);
    localStorage.setItem(PANEL_KEY, res.data.token);
    localStorage.setItem(USER_KEY, JSON.stringify(res.data.user));
    return res.data;
  }, [gwToken]);

  const logout = useCallback(async (alsoGateway = false) => {
    try {
      if (panelToken) await axios.post(`${API_BASE}/logout`, {}, { headers: { Authorization: `Bearer ${panelToken}` } });
    } catch (e) { /* ignore */ }
    setPanelToken(''); setUser(null);
    localStorage.removeItem(PANEL_KEY); localStorage.removeItem(USER_KEY);
    if (alsoGateway) {
      setGwToken(''); localStorage.removeItem(GW_KEY);
    }
  }, [panelToken]);

  const api = useCallback((method, path, body, opts = {}) => {
    const headers = { 'Content-Type': 'application/json' };
    if (panelToken) headers.Authorization = `Bearer ${panelToken}`;
    if (gwToken) headers['X-Gateway-Token'] = gwToken;
    const { headers: optionHeaders, ...rest } = opts;
    return axios({ method, url: `${API_BASE}${path}`, data: body, headers: { ...headers, ...(optionHeaders || {}) }, ...rest });
  }, [panelToken, gwToken]);

  const value = useMemo(() => ({
    gwToken, panelToken, user, bootChecked,
    gatewayLogin, panelLogin, logout, api,
  }), [gwToken, panelToken, user, bootChecked, gatewayLogin, panelLogin, logout, api]);

  return <PanelAuthContext.Provider value={value}>{children}</PanelAuthContext.Provider>;
}

export function usePanelAuth() {
  const ctx = useContext(PanelAuthContext);
  if (!ctx) throw new Error('usePanelAuth must be used within PanelAuthProvider');
  return ctx;
}

// Friendly error formatter — FastAPI 422 returns arrays, not strings
export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || 'Something went wrong';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}
